import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ragApi } from '@/services/ApiService'
import { agentsService } from '@/services/agentsService'
import type { DocumentPermission } from '@/types/agent'
import { queryKeys } from '../keys'

// The same name `brainApi` finds it by, and `agents/prompts/brain.py` creates it with.
const BRAIN_NAME = 'brain'

/** An agent, and what it may do with this document: `null` is not given it. */
export interface DocumentAgent {
  agent_id: string
  agent_name: string
  permiso: DocumentPermission | null
  /** The Brain is not given documents: it administers them all, and the one
   *  thing said about it is whether a document is hidden from it. */
  is_brain: boolean
}

/** «Quién lo usa»: every agent, with what it may do with this document
 *  (permisos-sobre-knowledge, 7.2). Every one and not only those that have it,
 *  because the screen offers the rest to give it to (a document nobody had yet
 *  showed nothing to do, seen in QA on 2026-09-29). Those that have it go first. */
export function useDocumentAgentsQuery(documentId: number | null) {
  return useQuery({
    queryKey: queryKeys.documents.agents(documentId ?? 0),
    queryFn: async (): Promise<DocumentAgent[]> => {
      const [assigned, agents] = await Promise.all([
        ragApi.getDocumentAgents(documentId as number) as Promise<DocumentAgent[]>,
        agentsService.listAgents(),
      ])
      const byId = new Map(assigned.map((row) => [row.agent_id, row.permiso]))
      return agents
        .map((agent) => ({
          agent_id: agent.id,
          agent_name: agent.display_name || agent.name,
          permiso: byId.get(agent.id) ?? null,
          is_brain: agent.name === BRAIN_NAME,
        }))
        .sort((a, b) => Number(b.permiso !== null) - Number(a.permiso !== null) || a.agent_name.localeCompare(b.agent_name))
    },
    enabled: documentId !== null,
  })
}

/** Changes what one agent may do with one document. The agent's set is replaced
 *  whole by the server, so it is read first and sent back with this one changed;
 *  `null` takes the document away from the agent. */
export function useSetDocumentPermission(documentId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ agentId, permiso }: { agentId: string; permiso: DocumentPermission | null }) => {
      const { documents } = await agentsService.getAgentDocuments(agentId)
      const permisos: Record<number, DocumentPermission> = {}
      documents.forEach((document) => {
        permisos[document.id] = document.permiso
      })
      if (permiso === null) delete permisos[documentId]
      else permisos[documentId] = permiso
      return agentsService.assignDocuments(agentId, Object.keys(permisos).map(Number), permisos)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.agents(documentId) })
    },
  })
}
